<template>
  <ProgressCircular v-if="loading" />
  <form v-else novalidate :disabled="disabled" @submit.prevent="submit()">
    <v-row no-gutters>
      <v-col cols="8">
        <v-card max-width="1200" flat color="transparent">
          <v-card-title class="mb-10">
            <FormHeader :loading="loading" :link="'page-historical-figures'" :title="getTitle ? `Редактировать ${getTitle}` : `Добавить историческую личность`
              " />
          </v-card-title>
          <v-card-text>
            <v-container>
              <PageContent>
                <!-- Name and slug -->
                <InputTitleSlug :is-loading="loading" :title="fields.name" :title-error="validationErrors.name"
                  :slug="fields.slug" :slug-error="validationErrors.slug" :isSlugEnabled="!isNew"
                  @change:title="fields.name = $event" @change:slug="fields.slug = $event"
                  @input:slug="$v.fields.slug.$touch" />
                <!-- Excerpt -->
                <v-row>
                  <v-col cols="12">
                    <span>Краткое описание</span>
                    <v-textarea outlined :disabled="loading" :loading="loading" v-model="fields.excerpt" />
                  </v-col>
                </v-row>
                <!-- Description -->
                <v-row>
                  <QuillEditorVue :cols="12" :content="fields.description" :loading="loading" :disabled="loading"
                    @input="fields.description = $event" @onChangeContent="$emit('change:descriptionForMobile', { fields, $event })"/>
                </v-row>
                <!-- Video URL -->
                <v-row>
                  <v-col cols="12">
                    <span>Видео url</span>
                    <v-text-field outlined :loading="loading" v-model="fields.videoUrl" />
                  </v-col>
                </v-row>
                <!-- thumbnail -->
                <v-row>
                  <v-col cols="12">
                    <MediaFilePicker :label="'Миниатюра'" :multiple="false" :file-type="$options.mediaTypesByKeys.IMAGE"
                      :value="fields.thumbnailId" @change="fields.thumbnailId = $event" />
                  </v-col>
                </v-row>
                <!-- Cover -->
                <v-row>
                  <v-col cols="12">
                    <MediaFilePicker :label="'Обложка'" :multiple="false" :file-type="$options.mediaTypesByKeys.IMAGE"
                      :value="fields.coverId" @change="fields.coverId = $event" />
                  </v-col>
                </v-row>
                <!-- Location -->
                <v-row>
                  <v-col cols="12">
                    <span>Расположение</span>
                    <LocationAutocomplete :address="fields.location" :loading="loading" @set-location="setLocation"
                      @update-location-part="updateLocation" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <SeoSettings :data="data" :loading="loading"
                      @on-input-seo="fields = Object.assign({}, fields, $event)" />
                  </v-col>
                </v-row>
              </PageContent>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel isShowSortOrder :isNew="isNew" :language="language" :translations="fields.translations"
          :entity-id="getDataId" :entity-type="'historicalFigure'" :sortOrder="fields.sortOrder"
          @delete="$emit('delete-historicalFigure')" @cancel="$router.push({ name: 'page-historical-figures' })"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)" @update-sort-order="fields.sortOrder = Number($event)">
          <template #info-append>
            <StatusPicker align="center" cols="12" title="Статус:" :disabled="loading" :loading="loading"
              :status="fields.status" @status:change="fields.status = $event" />
          </template>
          <template #body>
            <v-card flat>
              <v-card-subtitle class="grey--text"> Связи </v-card-subtitle>
            </v-card>
            <v-card-text>
              <v-row no-gutters>
                <v-col v-if="!!cities">
                  <span>Город</span>
                  <v-select outlined no-data-text="Список пуст" item-value="id" item-text="title" multiple
                    :value="fields.cities" :items="cities" @change="fields.cities = $event" />
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col v-if="!!places">
                  <span>Места</span>
                  <v-select outlined no-data-text="Список пуст" item-value="id" item-text="title" multiple
                    :value="fields.places" :items="places" @change="fields.places = $event" />
                </v-col>
              </v-row>
            </v-card-text>
          </template>
        </ManageSidePanel>
      </v-col>
    </v-row>
  </form>
</template>

<script>
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'

import { defaultDateFormat } from '@/configs/date.js'
import Geolocation from '@/utils/types/Geolocation.js'
import validationRules from './validation-rules'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'

import FormAbstract from '@/components/abstract/form'
import LocationAutocomplete from '@/components/location-autocomplete'
import SeoSettings from '@/components/seo-settings'
import ManageSidePanel from '@/components/manage-side-panel'
import MediaFilePicker from '@/components/media-file-picker'
import FormHeader from '@/components/form-header'
import InputTitleSlug from '@/components/inputs/InputTitleSlug'
import QuillEditorVue from '@/components/quill-editor'
import StatusPicker from '@/components/status-picker'

import placesQuery from '@/services/gql/types/Places/queries/placesGrid.gql'
import citiesQuery from '@/services/gql/types/City/queries/citiesGrid.gql'
import { mixForm } from '@/utils/form.mix.js'
import PageContent from '@/components/page-content/PageContent.vue'
import ProgressCircular from '@/components/progress-circular'

export default {
  name: 'FormHistoricalFigure',
  defaultDateFormat,
  validationRules,
  mediaTypesByKeys: mediaTypesByKeys(),
  extends: FormAbstract,
  mixins: [validationTranslations, prepareTranslations, mixForm],
  components: {
    PageContent,
    ManageSidePanel,
    LocationAutocomplete,
    InputTitleSlug,
    FormHeader,
    SeoSettings,
    MediaFilePicker,
    QuillEditorVue,
    StatusPicker,
    ProgressCircular
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    language: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    getTitle () {
      if (this.data) {
        return this.data?.name || ''
      }
      return null
    },
    validationRulesVars () {
      return { id: this.data?.id, ctx: this, isNew: this.isNew }
    },
    getDataId () {
      return this?.data?.id || null
    },
    isMutationLoading () {
      return (
        this.$async.createCulturalOrganization.$pending ||
        this.$async.updateCulturalOrganization.$pending
      )
    }

  },
  data () {
    return {
      fields: {
        name: null,
        slug: undefined,
        thumbnailId: null,
        coverId: null,
        location: new Geolocation(),
        status: null,
        cities: null,
        places: null,
        videoUrl: null,
        excerpt: null,
        description: null,
        translations: { ru: { isEnabled: true } },
        sortOrder: null
      },
      cities: null,
      places: null
    }
  },
  watch: {
    data (data) {
      this.populate(data)
      this.fields.cities = data?.cities?.map(city => city.id) || null
      this.fields.places = data?.places?.map(place => place.id) || null
      this.fields.thumbnailId = data?.thumbnail?.id || null
      this.fields.coverId = data?.cover?.id || null
    }
  },
  methods: {
    setLocation (location) {
      this.fields.location = location
    },
    updateLocation ({ value, fieldName }) {
      this.fields.location[fieldName] = value
    },
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }
      this.mixPrepareTranslations(payload)
      this.$emit('submit', payload)
    }
  },
  apollo: {
    cities: {
      fetchPolicy: 'network-only',
      query: citiesQuery,
      pollInterval: 30000,
      variables: {
        where: {
          status_not: 'DRAFT'
        }
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-historical-figures' })
        } else {
          this.cities = res?.data?.cities?.list || null
        }
      }
    },
    places: {
      fetchPolicy: 'network-only',
      query: placesQuery,
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-historical-figures' })
        } else {
          this.places = res?.data?.places?.list || null
        }
      }
    }
  }
}
</script>
